import { customElement, property, state } from 'lit/decorators.js';
import { CSSResult, html, LitElement, nothing, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { readJsonFromDom } from '../../scripts/utils.js';
import { WebshopOrderData, WebshopOrders } from '../../@types/ordered-banner.js';
import boxcheck from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-20_box-check.svg?element';
import basketCheck from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-20_basket-check.svg?element';
import fire from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_fire.svg?element';
import { addPresentationToIcon } from '../icon/icon-accessibility.js';
import styles from './product-personalization-banner.scss?inline';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { sharedCss } from '../../scripts/style/shared.js';
import { trackNudgeAvailable, trackNudgeViewed } from '../tracking/trackingLayer.js';

@customElement('ents-product-personalization-banner')
export class ProductPersonalizationBanner extends LitElement {
  @property({ type: String, attribute: 'mandanted-host' })
  private mandantedHost = '';

  @property({ type: Number, attribute: 'syan', reflect: false })
  private syan = 0;

  @property({ type: String, attribute: 'color-id', reflect: false })
  private colorId = '';

  @property({ type: String, attribute: 'article-in-cart-text', reflect: false })
  artileInCartText = '';

  @property({ type: String, attribute: 'last-order-link-text', reflect: false })
  lastOrderLinkText = '';

  @property({ type: String, attribute: 'nudge-text', reflect: false })
  nudgeText = '';

  @property({ type: String, attribute: 'nudge-type', reflect: false })
  nudgeType = '';

  @state() private isInCart = false;
  private webShopOrders: WebshopOrders | null = null;
  private nudgeTracked = false;
  private nudgeViewedTracked = false;
  private nudgeViewObserver: IntersectionObserver | null = null;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  public updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('syan') || changedProperties.has('colorId')) {
      this.loadArticlesInCart();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.webShopOrders = readJsonFromDom('#entsWebshopOrderData');
    this.loadArticlesInCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.nudgeViewObserver?.disconnect();
  }

  private loadArticlesInCart() {
    if (!this.mandantedHost) {
      return;
    }

    const cartPositionsUrl = `${this.mandantedHost}kauf/api/warenkorb/positionen`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 500);

    fetch(cartPositionsUrl, { signal: controller.signal })
      .then((response) => response.json())
      .then((data: CartPositions) => {
        this.isInCart = data.positionen.some((position) => {
          if (this.colorId) {
            return position.farbId === this.colorId;
          } else {
            return position.syan === this.syan;
          }
        });
      })
      .catch(() => {
        console.error('Failed to load cart positions');
        this.isInCart = false;
      })
      .finally(() => {
        clearTimeout(timeoutId);
        this.maybeTrackNudge();
      });
  }

  protected render(): TemplateResult {
    if (this.isInCart) {
      return this.renderArticleInCart();
    }

    const orderData = this.getOrderData();
    if (orderData) return this.renderAlreadyOrdered(orderData);

    if (this.nudgeText) return this.renderNudge();

    return html`${nothing}`;
  }

  private getOrderData(): WebshopOrderData | undefined {
    return this.colorId === '' ? this.webShopOrders?.[this.syan] : this.webShopOrders?.[this.syan + '-' + this.colorId];
  }

  private maybeTrackNudge(): void {
    const wouldShow = !!this.nudgeType && !this.isInCart && !this.getOrderData();
    if (this.nudgeTracked || !wouldShow) {
      return;
    }
    this.nudgeTracked = true;
    trackNudgeAvailable(this.nudgeType);
    this.observeNudgeViewport();
  }

  // the nudging badge sits directly above the a2c;
  // observe the a2c, since the badge is not rendered in the "default" test variant.
  private observeNudgeViewport(): void {
    const anchor = document.querySelector('[data-test-ents-cta]');
    if (!anchor) {
      return;
    }
    this.nudgeViewObserver = new IntersectionObserver(
      (entries) => {
        if (!this.nudgeViewedTracked && entries.some((entry) => entry.isIntersecting)) {
          this.nudgeViewedTracked = true;
          trackNudgeViewed(this.nudgeType);
          this.nudgeViewObserver?.disconnect();
        }
      },
      { threshold: 0 },
    );
    this.nudgeViewObserver.observe(anchor);
  }

  private renderArticleInCart() {
    return html`
      <div
        class="ents-product-personalization-banner bdk-alert"
        variant="neutral-light"
        data-test-ents-article-in-cart-banner
      >
        <span class="bdk-alert--icon">${addPresentationToIcon(basketCheck())}</span>
        <div class="bdk-text-s ents-product-personalization-banner__text">${this.artileInCartText}</div>
      </div>
    `;
  }

  private renderAlreadyOrdered(orderData: WebshopOrderData) {
    return html` <div
      class="ents-product-personalization-banner bdk-alert"
      variant="neutral-light"
      data-test-ents-ordered-banner
    >
      <span class="bdk-alert--icon">${addPresentationToIcon(boxcheck())}</span>
      <div class="bdk-text-s ents-product-personalization-banner__text">
        <div data-test-ents-ordered-banner-text>
          ${unsafeHTML(orderData.description)}
          <a href="${orderData.link}" class="bdk-link-inline">${this.lastOrderLinkText}</a>
        </div>
      </div>
    </div>`;
  }

  private renderNudge() {
    return html`
      <div class="ents-product-personalization-banner bdk-alert" variant="neutral-light" data-test-ents-nudge-banner>
        <span class="bdk-alert--icon">${addPresentationToIcon(fire())}</span>
        <div class="bdk-text-s ents-product-personalization-banner__text">${this.nudgeText}</div>
      </div>
    `;
  }
}
